import { use, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  CredentialList,
  type CredentialListState,
} from "@softmax/product-ux/account";
import {
  QueryViewerContext,
  useDiscardUnavailableData,
} from "@softmax/web-features/data";

import {
  credentialsQueryOptions,
  type CredentialTarget,
} from "./credentials.queries";
import type { CredentialOpening } from "./credential-intent";

const date = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});

export function CredentialTable({
  target,
  title,
  description,
  headingLevel,
  summary = false,
  onOpen,
}: {
  target: CredentialTarget;
  title: string;
  description: string;
  headingLevel: 2 | 4;
  summary?: boolean;
  onOpen(opening: CredentialOpening): void;
}) {
  const viewerId = use(QueryViewerContext);
  const options = credentialsQueryOptions({ viewerId, target });
  const query = useQuery(options);
  const unavailable = useDiscardUnavailableData({
    queryKey: options.queryKey,
    error: query.error,
  });
  const successFocusRef = useRef<HTMLButtonElement | null>(null);
  const open = async (
    intent: { action: "create" } | { action: "revoke"; id: string },
  ) => {
    if (query.isFetching || unavailable) return;

    const activeElement = document.activeElement;
    const invoker = activeElement instanceof HTMLElement ? activeElement : null;
    const before = query.dataUpdatedAt;
    const result = await query.refetch();

    if (!result.isSuccess || result.dataUpdatedAt <= before) return;

    if (intent.action === "create") {
      onOpen({
        intent: { action: "create", target, successFocusRef },
        invoker,
      });
    } else {
      const record = result.data.find((row) => row.id === intent.id);

      if (!record) {
        successFocusRef.current?.focus();
        return;
      }

      onOpen({
        intent: { action: "revoke", target, successFocusRef, record },
        invoker,
      });
    }
  };
  let state: CredentialListState = { status: "loading" };

  if (!unavailable && query.data) {
    state = {
      status: "ready",
      rows: query.data.map((row) => ({
        id: row.id,
        name: row.name,
        preview: row.token_preview,
        access: row.scopes.length ? row.scopes.join(", ") : "Read-only",
        created: date.format(new Date(row.created_at)),
        expires: row.expires_at
          ? `Expires ${date.format(new Date(row.expires_at))}`
          : "No expiry",
      })),
      refreshing: query.isFetching,
    };

    if (query.error)
      state = {
        ...state,
        refreshError: `Could not refresh credentials: ${query.error.message}`,
      };
  } else if (query.error) {
    state = { status: "error", message: query.error.message };
  }

  return (
    <CredentialList
      headingLevel={headingLevel}
      summary={summary}
      title={title}
      description={description}
      state={state}
      createControlRef={successFocusRef}
      onCreate={() => void open({ action: "create" })}
      onRevoke={(id) => void open({ action: "revoke", id })}
      onRefresh={() => {
        if (!query.isFetching) void query.refetch();
      }}
    />
  );
}
